import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useRealtimeTick } from "@/lib/realtime";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Send, Check, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/helpers";

export const errText = (e, d) => e?.response?.data?.detail || d;

export function SaleLines({ sales = [] }) {
  return (
    <div className="rounded-xl bg-slate-950/60 border border-slate-800 divide-y divide-slate-800 text-sm max-h-56 overflow-y-auto">
      {sales.map((x) => (
        <div key={x.id} className="flex justify-between gap-3 px-4 py-2">
          <span className="min-w-0"><span className="block font-semibold truncate">{x.product_name} ×{x.quantity}</span>
            <span className="block text-[11px] text-slate-500 truncate">{x.customer_name || "-"} · {x.user_name || "-"}</span></span>
          <span className="text-[11px] font-mono text-slate-500 shrink-0">{fmtDate(x.created_at)}</span>
        </div>
      ))}
    </div>
  );
}

export function RequestSentDialog({ open, count, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-sm text-center" data-testid="delete-request-sent-dialog">
        <DialogHeader>
          <div className="mx-auto h-16 w-16 rounded-2xl bg-blue-500/15 border border-blue-500/40 flex items-center justify-center mb-2">
            <Send size={28} className="text-blue-400" />
          </div>
          <DialogTitle className="text-center font-display text-2xl">Talebiniz Koordinatöre İletildi</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-400">{count > 1 ? `${count} satış` : "Satış"} için silme talebiniz gönderildi. Koordinatör onaylayınca satış silinecek ve size bildirim gelecek.</p>
        <Button onClick={onClose} className="w-full py-5 bg-blue-600 hover:bg-blue-500" data-testid="delete-request-sent-ok">Tamam</Button>
      </DialogContent>
    </Dialog>
  );
}

export async function decideRequest(rid, action) {
  try {
    await http.post(`/delete-requests/${rid}/${action}`);
    toast.success(action === "approve" ? "Talep onaylandı, satış silindi" : action === "reject" ? "Talep reddedildi" : "Talep geri çekildi");
    return true;
  } catch (e) { toast.error(errText(e, "İşlem yapılamadı")); return false; }
}

export function DeleteRequestsPanel() {
  const [rows, setRows] = useState([]);
  const tick = useRealtimeTick((m) => m.path.startsWith("/api/sales") || m.path.startsWith("/api/delete-requests") || m.path.startsWith("/api/notifications"));
  const load = () => http.get("/delete-requests").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [tick]);
  if (!rows.length) return null;
  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3 animate-fade-up" data-testid="delete-requests-panel">
      <div className="flex items-center gap-2 font-semibold text-amber-300"><Trash2 size={15} /> Bekleyen Silme Talepleri ({rows.length})</div>
      {rows.map((r) => (
        <div key={r.id} className="rounded-xl border border-slate-800 bg-slate-900/70 p-3 space-y-2" data-testid={`delete-request-${r.id}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm"><b>{r.requested_by_name}</b> <span className="text-slate-400">· {r.sale_ids.length} satış · {fmtDate(r.created_at)}</span></div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="border-slate-700" onClick={async () => (await decideRequest(r.id, "reject")) && load()} data-testid={`delete-request-reject-${r.id}`}><X size={14} className="mr-1" /> Reddet</Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-500 text-white" onClick={async () => (await decideRequest(r.id, "approve")) && load()} data-testid={`delete-request-approve-${r.id}`}><Check size={14} className="mr-1" /> Onayla ve Sil</Button>
            </div>
          </div>
          <SaleLines sales={r.sales} />
        </div>
      ))}
    </div>
  );
}
